import { createCacheFetch } from '@liquid-spirit/api/cache';
import { requestRaw } from './apiClient.js';
import maintainerMetrics from './maintainerMetrics';
import { dataFlowLog } from '../utils/dataFlowLogger';
import { credentialScope } from '../utils/credentialScope';
import { resolveStoredAuthToken } from '../utils/storage';
import {
  getActiveQueryOwner, isActiveQueryCredential, ownsQueryScope,
  subscribePrivateCacheInvalidation, subscribeQueryOwnerChanges,
} from '../queries/queryOwnership';

const privateCaches = new Map();
const cacheMetricMetadata = new Map();
const legacyKeys = new Set(['activitiesCache', 'events']);
const legacyPrefixes = ['maintainer:', 'activities:', 'events:', 'venues:', 'community:overview:', 'home-summary:', 'news:feed:', 'posts:item:', 'stats:summary:'];
const purgeLegacyStorage = () => {
  if (typeof window === 'undefined') return;
  for (const area of ['localStorage', 'sessionStorage']) {
    try {
      const storage = window[area];
      Object.keys(storage).filter(key => legacyKeys.has(key) || legacyPrefixes.some(prefix => key.startsWith(prefix)))
        .forEach(key => storage.removeItem(key));
    } catch { /* Optional storage is not an authority boundary. */ }
  }
};
const forgetLegacyKey = key => {
  if (!key) return;
  legacyKeys.add(key);
  if (typeof window === 'undefined') return;
  for (const area of ['localStorage', 'sessionStorage']) {
    try { window[area].removeItem(key); } catch { /* Storage is optional. */ }
  }
};
const retiredRequest = () => Object.assign(new Error('Private cache request aborted because its scope is no longer active'), {
  name: 'AbortError', code: 'CACHE_SCOPE_RETIRED',
});
const isCurrent = context => context.active && (context.owner
  ? ownsQueryScope(context.owner) && context.owner.communityId === getActiveQueryOwner()?.communityId
    && isActiveQueryCredential(context.credential)
  : !getActiveQueryOwner() && resolveStoredAuthToken() === context.storedToken);
const assertCurrent = context => { if (!isCurrent(context)) throw retiredRequest(); };
const retireCache = context => {
  context.active = false;
  context.keys.forEach(key => context.cache.deleteCached(key));
  if (privateCaches.get(context.id) === context) privateCaches.delete(context.id);
};
const clearPrivateCaches = () => {
  [...privateCaches.values()].forEach(retireCache);
  purgeLegacyStorage();
};
subscribeQueryOwnerChanges(clearPrivateCaches);
const resourcePath = path => String(path || '').replace(/^https?:\/\/[^/]+/i, '').split(/[?#]/)[0];
const resourceRevision = (context, path) => context.revisions.get(resourcePath(path)) || 0;
const invalidateResource = (context, path) => {
  const resource = resourcePath(path);
  if (!resource) return;
  context.revisions.set(resource, resourceRevision(context, path) + 1);
  context.paths.forEach((value, key) => { if (value === resource) context.cache.deleteCached(key); });
};
subscribePrivateCacheInvalidation(({ owner, credentialScope: scope, path }) => {
  privateCaches.forEach(context => {
    if (context.credentialScope === scope && context.owner?.actorId === owner?.actorId
      && context.owner?.communityId === owner?.communityId && context.owner?.sessionId === owner?.sessionId) {
      invalidateResource(context, path);
    }
  });
});
purgeLegacyStorage();
const metricOptions = {
  onMetricStart: ({ path, method, key }) => {
    const metric = maintainerMetrics.recordApiRequestMetricStart({ path, method, source: 'cacheFetch' });
    cacheMetricMetadata.set(metric, { path, method, key });
    dataFlowLog('cache-fetch.metric.start', { path, method, key, metricTracked: true });
    return metric;
  },
  onMetricEnd: (metric, details = {}) => {
    const metadata = cacheMetricMetadata.get(metric) || {};
    cacheMetricMetadata.delete(metric);
    dataFlowLog('cache-fetch.metric.end', {
      path: metadata.path || null, method: metadata.method || null, key: metadata.key || details.key || null,
      cacheStatus: details.cacheStatus || 'unknown', status: details.status || null,
      hasPayload: Boolean(details.payload),
      error: details.error ? (details.error.message || 'Request error') : null,
    });
    maintainerMetrics.recordApiRequestMetricEnd(metric, details);
  },
};
const requestCredential = options => {
  const authorization = Object.entries(options.headers || {}).find(([name]) => name.toLowerCase() === 'authorization');
  return authorization ? /^Bearer\s+(.+)$/i.exec(String(authorization[1]))?.[1] || null : resolveStoredAuthToken(options.token);
};
const currentCache = (options = {}) => {
  purgeLegacyStorage();
  const owner = getActiveQueryOwner();
  const credential = requestCredential(options);
  if (owner && !isActiveQueryCredential(credential)) throw retiredRequest();
  const opaqueCredential = credentialScope(credential);
  // Actual credentials live only in the closure, never the cache ID or disk.
  const id = JSON.stringify([owner?.actorId, owner?.communityId, owner?.sessionId, opaqueCredential]);
  let context = privateCaches.get(id);
  if (context && !isCurrent(context)) { retireCache(context); context = null; }
  if (!context) {
    context = { id, owner, credential, credentialScope: opaqueCredential, storedToken: resolveStoredAuthToken(),
      active: true, keys: new Set(), paths: new Map(), revisions: new Map() };
    context.cache = createCacheFetch({
      ...metricOptions,
      apiFetchRaw: async (path, requestOptions) => {
        assertCurrent(context);
        const result = await requestRaw(path, { ...requestOptions, token: context.credential });
        // Propagate the original denial even if the API notification already
        // retired this resource. Unrelated authorized paths remain valid.
        if ([401, 403].includes(result.response.status)) { invalidateResource(context, path); return result; }
        assertCurrent(context);
        return result;
      },
      defaultStaleMs: 60000, defaultRevalidate: true,
    });
    privateCaches.set(id, context);
  }
  return context;
};
export const getCached = (key, options = {}) => {
  // React can read after synchronous ownership rotation, before token effects.
  if (getActiveQueryOwner() && !isActiveQueryCredential(requestCredential(options))) return null;
  const context = currentCache(options);
  const value = context.cache.getCached(key);
  dataFlowLog('cache-fetch.get-cached', { key, hit: Boolean(value), hasFetchedAt: Boolean(value?.fetchedAt) });
  return value ? structuredClone(value) : null;
};
export const setCached = (key, data, options = {}) => {
  forgetLegacyKey(options.storageKey);
  const context = currentCache(options);
  context.keys.add(key);
  return structuredClone(context.cache.setCached(key, structuredClone(data)));
};
export const cacheFetch = async (path, options = {}) => {
  const key = options.key || path;
  forgetLegacyKey(options.storageKey);
  const context = currentCache(options);
  context.keys.add(key);
  context.paths.set(key, resourcePath(path));
  const revision = resourceRevision(context, path);
  const base = context.cache.getCached(key);
  dataFlowLog('cache-fetch.request', { path, method: options.method || 'GET', requestKey: key,
    useCache: options.useCache !== false, staleMs: options.staleMs ?? null, revalidate: options.revalidate, persist: false });
  try {
    const result = await context.cache.cacheFetch(path, {
      ...options, key, token: context.credential, persist: false, storageKey: undefined,
      ...(options.useCache === false ? { staleMs: 0, revalidate: true } : {}),
      // Immediate private SWR would hide an authoritative 401/403 response.
      allowStaleOnError: false,
    });
    assertCurrent(context);
    if (revision !== resourceRevision(context, path)) throw retiredRequest();
    return structuredClone(result);
  } catch (error) {
    const status = Number(error?.status || 0);
    const networkFailure = error?.name === 'ApiClientError' && !status && error.cause && error.cause.name !== 'AbortError';
    if (options.allowStaleOnError !== false && options.useCache !== false && base && isCurrent(context)
      && revision === resourceRevision(context, path) && !options.signal?.aborted && (status >= 500 || networkFailure)) {
      (options.validateResponse || (value => value))(base.data);
      return structuredClone(base.data);
    }
    throw error;
  }
};
export const deleteCached = (key, options = {}) => {
  forgetLegacyKey(options.storageKey);
  privateCaches.forEach(context => { context.cache.deleteCached(key); context.keys.delete(key); });
};
export const clearCachedByPrefix = prefix => {
  const normalized = String(prefix || '');
  if (!normalized) return;
  privateCaches.forEach(context => {
    context.cache.clearCachedByPrefix(normalized);
    context.keys.forEach(key => { if (String(key).startsWith(normalized)) context.keys.delete(key); });
  });
  purgeLegacyStorage();
};
export default { cacheFetch, getCached, setCached, deleteCached, clearCachedByPrefix };
